import { SidebarPanel, useAppStore } from '../../../store/appStore';
import { DroneQIcon, FileIcon, invoiceIcon as InvoiceIcon } from '@traceair/webapp-icons';
import { BlackOpacity07, List, ListItem } from '@traceair/tds';
import { classes, stylesheet } from 'typestyle';
import { trackAction } from '../../../libs/analytics/ActionTracking';
import { useUiStore } from '../../../store/uiStore';
import { useGuardUnsavedGcp } from '../../../hooks/useGuardUnsavedGcp';

const css = stylesheet({
  container: {
    background: 'none',
  },
  selectedItem: {
    background: BlackOpacity07,
  },
});

export const SidebarTopMenu = () => {
  const { selectedSidebarPanel, setSelectedSidebarPanel, pilot } = useAppStore();
  const { setIsMenuOpen } = useUiStore();
  const withGcpUnsavedChangesGuard = useGuardUnsavedGcp();

  /**
   * Handles sidebar tab selection with GCP unsaved changes guard.
   * If there are unsaved GCP form changes or pending uploads,
   * shows a confirmation modal before proceeding.
   *
   * @param panel - The sidebar panel to switch to (e.g., 'MISSIONS', 'UPLOADS').
   * @param actionName - Name of the action for analytics tracking.
   */
  const handleSelect = (panel: SidebarPanel, actionName: string) => {
    withGcpUnsavedChangesGuard(() => {
      trackAction(`${actionName} sidebar button click`, null);
      setSelectedSidebarPanel(panel);
      setIsMenuOpen(false);
    })();
  };

  return (
    <List rounded={true} className={css.container}>
      <ListItem
        className={classes(selectedSidebarPanel === 'MISSIONS' && css.selectedItem)}
        size="s"
        leftCell={<DroneQIcon />}
        title="My Missions"
        onClick={() => handleSelect('MISSIONS', 'Missions')}
      />

      {/* Gate on `pilot?.id` (not just `pilot`): the pilot-api responds to `GET /pilot` with
          an empty body when the signed-in user doesn't resolve to a UAS Pilots record, which
          survives `response?.data ?? null` as an empty-string-ish value and would wrongly open
          both tabs. Requiring an id means these tabs only appear once a real pilot resolved. */}
      {pilot?.id && (
        <>
          {/* `invoicesAvailable=false` means the pilot-api only matched this user via a loose
              alt-email list entry — PUT /invoice would 404 and GET /pilot-costs would be empty,
              so the tab is hidden rather than leading the pilot into a dead end. */}
          {pilot.invoicesAvailable !== false && (
            <ListItem
              className={classes(selectedSidebarPanel === 'INVOICING' && css.selectedItem)}
              size="s"
              leftCell={<InvoiceIcon />}
              title="Invoices"
              onClick={() => handleSelect('INVOICING', 'Invoicing')}
            />
          )}

          <ListItem
            className={classes(selectedSidebarPanel === 'DOCUMENTS' && css.selectedItem)}
            size="s"
            leftCell={<FileIcon />}
            title="Documents"
            onClick={() => handleSelect('DOCUMENTS', 'Documents')}
          />
        </>
      )}
    </List>
  );
};
